import {DetailsFieldsCard} from '@/components/ui/details-atoms/DetailsAtoms'
import {Text} from '@/components/ui/text/Text'
import {Trans, useTranslation} from 'react-i18next'
import {useGetAbsences} from '@/features/accesses/queries/useGetAbsences'
import {useOutletContext} from 'react-router-dom'
import {ProjectExtended} from '../../types'
import {useGetPlannedAccessesDetails} from '@/features/planned-accesses/queries/useGetPlannedAccessesDetails'
import {useAbsencesCalculations} from '../../hooks/useAbsencesCalculations'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon, CheckCircleIcon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'

export const AbsencesCard = () => {
    const {t} = useTranslation()
    const {project} = useOutletContext<{project: ProjectExtended}>()
    const {palette} = useTheme()

    const {data: absences, isLoading: isLoadingAbsences} = useGetAbsences({
        params: {
            projectId: project.id,
            page: 1,
            limit: 99999
        }
    })

    const {data: plannedAccesses, isLoading: isLoadingPlannedAccesses} = useGetPlannedAccessesDetails({
        urlParams: {
            projectId: project.id
        }
    })

    const isLoading = isLoadingAbsences || isLoadingPlannedAccesses

    const {totalAbsencesPaid, percentage, maxAbsencePercentage} = useAbsencesCalculations({
        project,
        absences,
        plannedAccesses
    })

    return (
        <DetailsFieldsCard size="md" direction="column" gap={2}>
            {isLoading ? (
                <>
                    <Flexbox align="center" gap={1}>
                        <Skeleton width="180px" height="20px" />
                        <Skeleton width="16px" height="16px" rx="8" ry="8" />
                    </Flexbox>
                    <Skeleton width="240px" height="30px" />
                </>
            ) : (
                <>
                    <Flexbox align="center" gap={1}>
                        <Text palette="neutral" shade="600" weight="400" size="textSm">
                            {t('projects:absences_threshold_percentage', {percentage})}
                        </Text>
                        <Tooltip
                            variant="light"
                            content={t('projects:absences_threshold_percentage_tooltip', {
                                maxAssencePercentage: maxAbsencePercentage
                            })}
                        >
                            <Flexbox>
                                {percentage < maxAbsencePercentage ? (
                                    <CheckCircleIcon size={16} color={palette.success[600]} />
                                ) : (
                                    <AlertCircleIcon size={16} color={palette.orange[600]} />
                                )}
                            </Flexbox>
                        </Tooltip>
                    </Flexbox>
                    <Text palette="neutral" shade="500" weight="500" size="textXs">
                        {absences?.length && absences?.length > 0 ? (
                            <Trans
                                i18nKey="projects:absence_description"
                                values={{count_total: absences?.length, count: totalAbsencesPaid}}
                                components={[
                                    <Text palette="neutral" shade="950" weight="500" size="displayXs" render="span" />
                                ]}
                            />
                        ) : (
                            <>-</>
                        )}
                    </Text>
                </>
            )}
        </DetailsFieldsCard>
    )
}
